import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Wand2, Copy, ChevronsUpDown, Check } from "lucide-react";

const CloneCombobox = ({ t, orgUsers, cloneId, setCloneId }) => {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => orgUsers.find((u) => u.id === cloneId), [orgUsers, cloneId]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} data-testid="onboard-clone-select"
          className="w-full justify-between rounded-sm border-zinc-300 font-normal sm:w-[28rem]">
          <span className="truncate text-left">
            {selected ? <>{selected.displayName} — {selected.userPrincipalName}{selected.leaved ? " · LEAVED" : ""}</> : <span className="text-zinc-400">{t("onb.cloneSelect")}</span>}
          </span>
          <ChevronsUpDown size={15} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] rounded-sm p-0" align="start">
        <Command filter={(value, search) => {
          const u = orgUsers.find((x) => x.id === value);
          const hay = `${u?.displayName || ""} ${u?.userPrincipalName || ""}${u?.leaved ? " leaved" : ""}`.toLowerCase();
          return hay.includes(search.toLowerCase()) ? 1 : 0;
        }}>
          <CommandInput placeholder={t("onb.cloneSearch")} data-testid="onboard-clone-search" />
          <CommandList>
            <CommandEmpty>{t("onb.cloneNoMatch")}</CommandEmpty>
            {orgUsers.map((u) => (
              <CommandItem key={u.id} value={u.id} data-testid={`onboard-clone-opt-${u.id}`}
                onSelect={(v) => { setCloneId(v); setOpen(false); }}>
                <Check size={15} className={`mr-2 shrink-0 ${cloneId === u.id ? "opacity-100" : "opacity-0"}`} />
                <span className="truncate">{u.displayName} — {u.userPrincipalName}</span>
                {u.leaved && <span className="ml-2 shrink-0 border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-800">{t("leaved.badge")}</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const Step1Start = ({ t, orgUsers, cloneId, setCloneId, cloneFrom, cloning, templates, f, applyTemplate }) => (
  <div className="space-y-8">
    <div>
      <div className="mb-1 flex items-center gap-2 text-sm font-bold text-zinc-900"><Copy size={16} className="text-primary" /> {t("onb.cloneTitle")}</div>
      <p className="mb-4 text-sm text-zinc-500">{t("onb.cloneHint")}</p>
      <div className="flex flex-col gap-3 sm:flex-row">
        <CloneCombobox t={t} orgUsers={orgUsers} cloneId={cloneId} setCloneId={setCloneId} />
        <Button type="button" variant="outline" onClick={cloneFrom} disabled={!cloneId || cloning} data-testid="onboard-clone-btn" className="shrink-0 rounded-sm border-zinc-300">
          <Copy size={15} className="mr-1.5" /> {cloning ? t("onb.cloning") : t("onb.cloneBtn")}
        </Button>
      </div>
    </div>
    <div className="border-t border-zinc-100 pt-6">
      <div className="mb-1 flex items-center gap-2 text-sm font-bold text-zinc-900"><Wand2 size={16} className="text-primary" /> {t("onb.roleTemplate")}</div>
      <p className="mb-4 text-sm text-zinc-500">{t("onb.roleTemplateOpt")}</p>
      <Select value={f.roleTemplateId} onValueChange={applyTemplate}>
        <SelectTrigger className="rounded-sm border-zinc-300" data-testid="onboard-template"><SelectValue placeholder={t("onb.noTemplate")} /></SelectTrigger>
        <SelectContent>{templates.map((tm) => <SelectItem key={tm.id} value={tm.id}>{tm.name} — {tm.department}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);
